import { useRef, useState, useMemo, useEffect, useCallback } from 'react'
import {
  Box,
  Button,
  Card,
  CardHeader,
  CardBody,
  Heading,
  HStack,
  VStack,
  Text,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Badge,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Tabs,
  TabList,
  Tab,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Spinner,
  Icon,
  Tag,
  TagLabel,
  Wrap,
} from '@chakra-ui/react'
import { Search, Plus, ChevronLeft } from 'lucide-react'
import { useNavigate, useParams } from 'react-router-dom'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { DataTable, type DataTableRef, type DataTableOptions } from '@/components/tables'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { useAccessCheck } from '@/hooks/useAccess'
import { apiClient } from '@/lib/api/client'
import { useThemeColors } from '@/hooks/useThemeColors'
import {
  sessionListStateKey,
  syncSearchInputRef,
  useSessionListState,
} from '@/hooks/useSessionListState'
import { createNameRenderer } from '@/lib/utils/tableRenderers'
import { ActionButtons } from '@/components/common/ActionButtons'

type TabValue = 'all' | 'trash'

export default function WaybillsPage() {
  const colors = useThemeColors()
  const navigate = useNavigate()
  const { bookingId } = useParams<{ bookingId: string }>()
  const tableRef = useRef<DataTableRef>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const { showError, showSuccess, showWarning } = useToastNotification()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { isOpen: isContainersModalOpen, onOpen: onContainersModalOpen, onClose: onContainersModalClose } = useDisclosure()
  const { isOpen: isBookingCompleteModalOpen, onOpen: onBookingCompleteModalOpen, onClose: onBookingCompleteModalClose } = useDisclosure()
  const {
    isOpen: isDeletionNotAllowedOpen,
    onOpen: onOpenDeletionNotAllowed,
    onClose: onCloseDeletionNotAllowed,
  } = useDisclosure()
  const access = useAccessCheck()

  const [activeTab, setActiveTab] = useState<TabValue>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [bookingInfo, setBookingInfo] = useState<any>(null)
  const [isLoadingBooking, setIsLoadingBooking] = useState(true)
  const [modalMessage, setModalMessage] = useState('')
  const [selectedWaybillId, setSelectedWaybillId] = useState<number | null>(null)
  const [totalRows, setTotalRows] = useState(0)
  const [totalTrash, setTotalTrash] = useState(0)
  const [bulkAction, setBulkAction] = useState('')
  const [isBulkProcessing, setIsBulkProcessing] = useState(false)
  const [containers, setContainers] = useState<any[]>([])
  const [isLoadingContainers, setIsLoadingContainers] = useState(false)
  const [selectedWaybillForContainers, setSelectedWaybillForContainers] = useState<{ waybillId: number; bookingId: number } | null>(null)
  const refreshCountsTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  useSessionListState({
    key: bookingId ? sessionListStateKey(`waybills-${bookingId}`) : null,
    values: {
      activeTab,
      searchQuery,
    },
    onRestore: (raw) => {
      if (raw.activeTab === 'all' || raw.activeTab === 'trash') {
        setActiveTab(raw.activeTab)
      }
      if (typeof raw.searchQuery === 'string') {
        setSearchQuery(raw.searchQuery)
        syncSearchInputRef(searchRef, raw.searchQuery)
      }
    },
  })

  useEffect(() => {
    if (!bookingId) {
      navigate('/bookings', { replace: true })
      return
    }

    const fetchBookingInfo = async () => {
      setIsLoadingBooking(true)
      try {
        const { data } = await apiClient.get(`/bookings/${bookingId}`)
        const bookingData = data?.data || data || {}
        setBookingInfo(bookingData)
      } catch (error: any) {
        showError(error)
        navigate('/bookings', { replace: true })
      } finally {
        setIsLoadingBooking(false)
      }
    }

    fetchBookingInfo()
  }, [bookingId, navigate, showError])

  const bookingIsComplete =
    bookingInfo != null && (bookingInfo.is_complete === 1 || bookingInfo.is_complete === true)

  const tableOptions: DataTableOptions = useMemo(
    () => ({
      dataFields: {
        waybill_number: {
          name: 'Waybill Number',
          maxWidth: '150px',
          withSort: true,
        },
        transaction_date: {
          name: 'Transaction Date',
          maxWidth: '120px',
          withSort: true,
        },
        shipping_line: {
          name: 'Shipping Line',
          maxWidth: '200px',
          withSort: true,
          sortField: 'shipping_line_id',
        },
        driver: {
          name: 'Driver',
          maxWidth: '200px',
          withSort: true,
          sortField: 'driver_id',
        },
        truck_plate_number: {
          name: 'Truck Plate Number',
          maxWidth: '120px',
          withSort: true,
        },
        container_size: {
          name: 'Container Size',
          maxWidth: '100px',
        },
        container_type: {
          name: 'Container Type',
          maxWidth: '100px',
        },
        container_numbers: {
          name: 'Container Numbers',
          maxWidth: '180px',
        },
        total_expense: {
          name: 'Total Expense',
          maxWidth: '120px',
        },
        total_rate_per_client: {
          name: 'Total Rate per Client',
          maxWidth: '120px',
        },
        prepared_by: {
          name: "Prepared By",
          maxWidth: "150px",
          withSort: true,
        },
      },
      dataSource: activeTab === 'trash' ? '/archived/waybill-details' : '/waybill-details',
      primaryKey: 'id',
      softDelete: true,
      displayInModal: false,
      noRecordText: 'No waybill found',
      editPath: (row: any) => `/bookings/waybills/${bookingId}/${row.id}`,
      showCheckboxes: true,
      actions: undefined,
      otherActions: [
        {
          name: 'View Containers',
          onClick: (row: any) => {
            if (row.booking_id && row.id) {
              setSelectedWaybillForContainers({ waybillId: row.id, bookingId: row.booking_id })
              onContainersModalOpen()
            }
          },
        },
      ],
      isDeleteAllowedForRow: () => !bookingIsComplete,
      onDeleteNotAllowed: () => onOpenDeletionNotAllowed(),
    }),
    [bookingId, activeTab, bookingIsComplete, onContainersModalOpen, onOpenDeletionNotAllowed]
  )

  const permissions = useMemo(
    () => ({
      can_edit: access.can_edit,
      can_delete: access.can_delete,
      can_create: access.can_create,
      can_view: access.can_view,
      can_export: false,
      can_import: false,
    }),
    [access]
  )

  const tableParams = useMemo(
    () => ({
      search: searchQuery,
      booking_id: bookingId || undefined,
    }),
    [searchQuery, bookingId]
  )

  // Fetch counts separately to ensure they're filtered by booking_id
  const fetchCounts = useCallback(async () => {
    if (!bookingId) return

    try {
      // Fetch all waybills count for this booking
      const { data: allData } = await apiClient.get('/waybill-details', {
        params: { booking_id: bookingId, per_page: 10 },
      })
      const allCount = allData?.meta?.total || allData?.total || 0

      // Fetch trashed waybills count for this booking
      const { data: trashData } = await apiClient.get('/archived/waybill-details', {
        params: { booking_id: bookingId, per_page: 10 },
      })
      const trashCount = trashData?.meta?.total || trashData?.total || 0

      setTotalRows(allCount)
      setTotalTrash(trashCount)
    } catch (error: any) {
      // Silently fail - counts will be updated by DataTable if available
      console.error('Failed to fetch waybill counts:', error)
    }
  }, [bookingId])

  // Debounced refresh counts function to prevent too many API calls
  const refreshCountsDebounced = useCallback(() => {
    if (refreshCountsTimeoutRef.current) {
      clearTimeout(refreshCountsTimeoutRef.current)
    }
    refreshCountsTimeoutRef.current = setTimeout(() => {
      fetchCounts()
    }, 200)
  }, [fetchCounts])

  useEffect(() => {
    fetchCounts()
  }, [fetchCounts])

  const showSubSubCallback = useMemo(
    () => (all: number, trashed: number) => {
      // When DataTable reloads (after single actions like delete/restore), refresh our filtered counts
      // Use debounced refresh to prevent too many API calls
      refreshCountsDebounced()
    },
    [refreshCountsDebounced]
  )

  const customRenderers = useMemo(
    () => ({
      shipping_line: createNameRenderer(true),
      driver: (value: any) => {
        let driver = ''
        if (!value || typeof value === 'object') {
          driver = value ? `${value.first_name || ''} ${value.last_name || ''}`.trim() || '' : ''
        } else {
          driver = value
        }

        return (
          <Box
            maxW="200px"
            wordBreak="break-word"
            whiteSpace="normal"
            overflowWrap="break-word"
          >
            {driver || ''}
          </Box>
        )
      },
      prepared_by: (value: any) => {
        return (
          <Box
            maxW="150px"
            wordBreak="break-word"
            whiteSpace="normal"
            overflowWrap="break-word"
          >
            {value || ''}
          </Box>
        )
      },
      container_size: (value: any) => {
        return (
          <Box
            maxW="100px"
            wordBreak="break-word"
            whiteSpace="normal"
            overflowWrap="break-word"
          >
            {value || ''}
          </Box>
        )
      },
      transaction_date: (value: string) => {
        if (!value) return ''
        return new Date(value).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        })
      },
      container_numbers: (value: any) => {
        if (!value) return <span></span>

        const containerArray = Array.isArray(value)
          ? value
          : typeof value === 'string'
          ? value.split(',').map((item: string) => item.trim()).filter((item: string) => item.length > 0)
          : []

        if (containerArray.length === 0) return <span></span>

        return (
          <Wrap spacing={1}>
            {containerArray.map((containerNumber: string, index: number) => (
              <Tag
                key={index}
                size="sm"
                borderRadius="2px"
                variant="subtle"
                colorScheme="cyan"
                px={2}
                py={1}
              >
                <TagLabel>{containerNumber}</TagLabel>
              </Tag>
            ))}
          </Wrap>
        )
      },
    }),
    []
  )

  const handleTabChange = (index: number) => {
    setActiveTab(index === 0 ? 'all' : 'trash')
    setSearchQuery('')
    if (searchRef.current) {
      searchRef.current.value = ''
    }
    tableRef.current?.clearPage()
    // Refresh counts when tab changes (items might have moved between tabs after actions)
    refreshCountsDebounced()
  }

  const handleSearch = async () => {
    const query = searchRef.current?.value || ''
    setSearchQuery(query)
    tableRef.current?.applyFilter('search', query)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  const handleBulkActionClick = () => {
    if (!bulkAction) {
      showWarning('Please select an action first')
      return
    }
    const selectedIds = tableRef.current?.getSelectedRows() || []
    if (selectedIds.length === 0) {
      showWarning('Please select at least one item')
      return
    }

    if (bulkAction === 'delete' && bookingIsComplete) {
      onOpenDeletionNotAllowed()
      return
    }

    setSelectedWaybillId(null) // Clear single delete selection
    if (bulkAction === 'delete' && activeTab === 'trash') {
      setModalMessage('You are about to permanently delete these items from your site. This action cannot be undone.')
    } else {
      setModalMessage('Are you sure to apply this change?')
    }
    onOpen()
  }

  const handleConfirmBulkAction = async () => {
    const selectedIds = tableRef.current?.getSelectedRows() || []
    if (selectedIds.length === 0) return

    if (bulkAction === 'delete' && bookingIsComplete) {
      onOpenDeletionNotAllowed()
      return
    }

    setIsBulkProcessing(true)
    try {
      let url = ''
      let payload: any = { ids: selectedIds }
      let successMessage = 'Action completed successfully'

      if (bulkAction === 'restore') {
        url = '/waybill-details/bulk/restore'
        successMessage = 'Waybills restored successfully'
      } else if (bulkAction === 'delete') {
        url =
          activeTab === 'trash'
            ? '/waybill-details/bulk/force-delete'
            : '/waybill-details/bulk/delete'
        successMessage =
          activeTab === 'trash' ? 'Waybills permanently deleted successfully' : 'Waybills moved to trash successfully'
      } else {
        showWarning('Unsupported bulk action')
        setIsBulkProcessing(false)
        return
      }

      await apiClient.post(url, payload)
      showSuccess('Success', successMessage)
      tableRef.current?.reload()
      setBulkAction('')
      onClose()
      // Refresh counts after bulk action (with delay to ensure backend has processed)
      refreshCountsDebounced()
    } catch (error: any) {
      showError(error)
    } finally {
      setIsBulkProcessing(false)
    }
  }

  // Auto-switch to All tab when Trash becomes empty
  useEffect(() => {
    if (activeTab === 'trash' && totalTrash === 0) {
      // Switch to All tab and refresh
      setActiveTab('all')
      setSearchQuery('')
      if (searchRef.current) {
        searchRef.current.value = ''
      }
      tableRef.current?.clearPage()
      // Small delay to ensure tab switch completes before reload
      const timeoutId = setTimeout(() => {
        tableRef.current?.reload()
        // Refresh counts after tab switch
        refreshCountsDebounced()
      }, 100)
      
      return () => {
        clearTimeout(timeoutId)
      }
    }
  }, [totalTrash, activeTab, refreshCountsDebounced])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (refreshCountsTimeoutRef.current) {
        clearTimeout(refreshCountsTimeoutRef.current)
      }
    }
  }, [])

  // Fetch containers when modal opens
  useEffect(() => {
    let isMounted = true

    const fetchContainers = async () => {
      if (isContainersModalOpen && selectedWaybillForContainers) {
        setIsLoadingContainers(true)
        try {
          const { data } = await apiClient.get('/containers', {
            params: {
              booking_id: selectedWaybillForContainers.bookingId,
              waybill_id: selectedWaybillForContainers.waybillId,
            },
          })
          if (!isMounted) return
          const containersData = data?.data || data || []
          setContainers(containersData)
        } catch (error: any) {
          if (isMounted) {
            showError(error)
            setContainers([])
          }
        } finally {
          if (isMounted) {
            setIsLoadingContainers(false)
          }
        }
      }
    }

    fetchContainers()

    return () => {
      isMounted = false
    }
  }, [isContainersModalOpen, selectedWaybillForContainers, showError])

  const handleCloseContainersModal = () => {
    onContainersModalClose()
    setContainers([])
    setSelectedWaybillForContainers(null)
  }

  const handleAddWaybill = () => {
    if (bookingInfo?.is_complete === true) {
      onBookingCompleteModalOpen()
    } else {
      navigate(`/bookings/waybills/${bookingId}/create`)
    }
  }

  if (isLoadingBooking) {
    return (
      <AdminLayout>
        <Box p={4}>
          <Card>
            <CardBody>
              <VStack spacing={4} py={8}>
                <Text>Loading waybills...</Text>
              </VStack>
            </CardBody>
          </Card>
        </Box>
      </AdminLayout>
    )
  }

  if (!bookingInfo) {
    return null
  }

  return (
    <AdminLayout>
      <Box p={4} h="100%" overflow="hidden" display="flex" flexDirection="column">
        <VStack spacing={2} align="stretch" flex="1" overflow="hidden" minH={0}>
          <Card flexShrink={0} display="flex" flexDirection="column" flex="1" minH={0} overflow="hidden">
            <CardHeader flexShrink={0} py={3}>
              <HStack spacing={2} align="center" mb={1}>
                <IconButton 
                  aria-label="Back" 
                  icon={<Icon as={ChevronLeft} boxSize={6} />} 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => navigate('/bookings')} 
                />
                <Heading size="md">Waybills</Heading>
              </HStack>
              <Text fontSize="sm" color={colors.text} ml={7}>
                Manage waybills for booking: <strong>{bookingInfo.reference_number || `Booking #${bookingId}`}</strong>
              </Text>
            </CardHeader>

            <CardHeader py={0} px={0} flexShrink={0}>
              <Box pt={2} pb={0}>
                <HStack justify="space-between" align="flex-end" spacing={4} px={4} width="100%">
                  <Tabs index={activeTab === 'all' ? 0 : 1} onChange={handleTabChange}>
                    <TabList borderBottom="none">
                      <Tab>
                        All{' '}
                        <Badge ml={2} colorScheme="blue">
                          {totalRows}
                        </Badge>
                      </Tab>
                      {totalTrash > 0 && (
                        <Tab>
                          Trash{' '}
                          <Badge ml={2} colorScheme="red">
                            {totalTrash}
                          </Badge>
                        </Tab>
                      )}
                    </TabList>
                  </Tabs>
                  
                  <Box pb={2}>
                    <ActionButtons
                      pathname={`/bookings/waybills/${bookingId}`}
                      createLabel="Add Waybill"
                      onCreate={handleAddWaybill}
                    />
                  </Box>

                </HStack>
                <Box borderBottom="2px" borderColor={colors.border} mx={4} />
              </Box>
            </CardHeader>

            <CardHeader py={1} pt={3} pb={2} flexShrink={0}>
              <HStack spacing={4} flexWrap="wrap">
                {access.can_delete && (
                  <HStack spacing={2} minW="250px">
                    <Select
                      placeholder="Bulk actions"
                      size="sm"
                      value={bulkAction}
                      onChange={(e) => {
                        e.stopPropagation()
                        setBulkAction(e.target.value)
                      }}
                    >
                      {activeTab === 'trash' && <option value="restore">Restore</option>}
                      {activeTab === 'trash' && <option value="delete">Delete Permanently</option>}
                      {activeTab === 'all' && <option value="delete">Delete</option>}
                    </Select>
                    <Button size="sm" colorScheme="blue" onClick={handleBulkActionClick}>
                      Apply
                    </Button>
                  </HStack>
                )}

                <Box flex="0 1 auto" minW="250px" maxW="400px">
                  <InputGroup size="sm">
                    <Input
                      ref={searchRef}
                      placeholder="Search waybills..."
                      onKeyDown={handleKeyDown}
                      defaultValue={searchQuery}
                      pr="2.5rem"
                    />
                    <InputRightElement width="2.5rem">
                      <IconButton
                        aria-label="Search"
                        icon={<Search size={16} />}
                        size="sm"
                        h="1.75rem"
                        w="1.75rem"
                        onClick={handleSearch}
                        variant="ghost"
                      />
                    </InputRightElement>
                  </InputGroup>
                </Box>
              </HStack>
            </CardHeader>

            <CardBody
              p={0}
              pt={1}
              pb={4}
              mx={5}
              flex="1"
              overflow="hidden"
              display="flex"
              flexDirection="column"
              minH={0}
              position="relative"
            >
              <Box h="100%" display="flex" flexDirection="column" minH={0}>
                <DataTable
                  ref={tableRef}
                  options={tableOptions}
                  params={tableParams}
                  access={permissions}
                  setSubSub={showSubSubCallback}
                  customRenderers={customRenderers}
                />
              </Box>
            </CardBody>
          </Card>
        </VStack>
      </Box>

      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirmation</ModalHeader>
          <ModalBody>
            <Text>{modalMessage}</Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isBulkProcessing}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleConfirmBulkAction} isLoading={isBulkProcessing} loadingText="Processing...">
              Confirm
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* View Containers Modal */}
      <Modal isOpen={isContainersModalOpen} onClose={handleCloseContainersModal} isCentered size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Containers</ModalHeader>
          <ModalBody>
            {isLoadingContainers ? (
              <VStack spacing={4} py={8}>
                <Spinner size="xl" color="blue.500" />
                <Text>Loading containers...</Text>
              </VStack>
            ) : (
              <TableContainer>
                <Table variant="simple" size="sm" borderWidth="1px" borderColor="gray.300">
                  <Thead bg="gray.100">
                    <Tr>
                    <Th borderWidth="1px" borderColor="gray.300" bg="gray.100">Container Number</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {containers.length === 0 ? (
                      <Tr>
                        <Td colSpan={1} textAlign="center" color="gray.400" fontStyle="italic">
                          No containers found
                        </Td>
                      </Tr>
                    ) : (
                      containers.map((container, index) => (
                        <Tr key={container.id || index}>
                          <Td borderWidth="1px" borderColor="gray.300">{container.container_number || container.container_name || 'N/A'}</Td>
                        </Tr>
                      ))
                    )}
                  </Tbody>
                </Table>
              </TableContainer>
            )}
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={handleCloseContainersModal}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Booking Complete Warning Modal */}
      <Modal isOpen={isBookingCompleteModalOpen} onClose={onBookingCompleteModalClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Booking Completed</ModalHeader>
          <ModalBody>
            <Text>This booking is already completed. You can no longer add a waybill to it.</Text>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={onBookingCompleteModalClose}>
              OK
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isDeletionNotAllowedOpen} onClose={onCloseDeletionNotAllowed} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Deletion not allowed</ModalHeader>
          <ModalBody>
            <Text>
              This booking is already completed. Deletion of waybill(s) is not allowed for completed bookings.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={onCloseDeletionNotAllowed}>
              OK
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </AdminLayout>
  )
}

